import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    OktaAuth as OkAuth,
    toRelativeUrl,
} from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';

// Okta signin widget CSS has to be absolute path
// https://devforum.okta.com/t/unable-to-compile-with-okta-okta-signin-widget-dist-css-okta-sign-in-min-css/24164
import '../../../../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';

import { ThemeProvider as MUIThemeProvider } from '@mui/styles';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/styled-engine-sc';
import CSSBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

import ReactHtmlParser from 'html-react-parser';
import { get as _get } from 'lodash';

import { Copyright } from '../../components/messages/footer';

import Themes from '../../data/themes';
import Auth, { UISettings } from '../../data/auth-service';

import useGoHome from './home';
import { SAMLLogin } from './saml';
import { BRCLogin } from './bricata';
import { OktaLogin } from './okta';
import Typography from '@mui/material/Typography';

const LoginWrapper = () => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Themes.DarkTheme}>
                <MUIThemeProvider theme={Themes.DarkTheme}>
                    <Login />
                </MUIThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

const APP_NAME = process.env.BRICATA_APPLIANCE_TYPE === 'mc2' ? 'MC2' : 'CMC';

const Login = (params) => {
    // const classes = useStyles();
    const theme = useTheme();
    const [preferredSource, setPreferredSource] = useState('brc');
    const [availableSource, setAvailableSource] = useState('');
    const [available, setAvailable] = useState();

    const goHome = useGoHome();
    const navigate = useNavigate();

    // login is not under app, cannot use global data context
    const [systemSettings, setSystemSettings] = useState({ ui: { lookups: {}, messages: {} } });

    const { state } = useLocation();

    const {
        BRICATA_VERSION
    } = process.env;

    const handleUpdatePreferredSource = src => {
        Auth.setPreferredSource(src);
        setPreferredSource(src);
    };

    const friendlySrc = src => {
        return src === 'saml' ? 'Single Sign-On'
            : src === 'okta' ? 'Okta'
                : src === 'brc' ? 'Bricata'
                    : '';
    };

    const FetchAvailableSource = useCallback(() => {
        UISettings.availableAuthSource().then(a => {
            const availableSource =
                a === 'saml' ? (preferredSource === 'brc' ? 'saml' : 'brc')
                    : a === 'okta' ? (preferredSource === 'brc' ? 'okta' : 'brc')
                        : '';
            setAvailableSource(availableSource);
        }, err => {
            console.error('Available source error ::: ', err);
            setAvailableSource('');
        });
    }, [preferredSource]);

    const handleLoginSuccess = useCallback(res => {
        goHome(res.user);
    }, [goHome]);

    const handleLoginError = useCallback((error = 'Login failed, please try again') => {
        // error string might contain JSON
        const err = error;
        try {
            const e = JSON.parse(err);
            const msg = e.error || error;
            // this is the only error we want to leak to the user
            // AND the API should never return any other reason either
            if (msg.includes('standby')) error = 'This CMC is running in standby mode. Cannot serve requests.';
        } catch (err) {
            console.debug(err);
        }

        navigate('/errorback', {
            replace: true,
            state: { error },
        });
    }, [navigate]);

    useEffect(() => {
        Auth.isAvailable()
            .then(res => setAvailable(res.available), () => setAvailable(false))
            .catch(() => setAvailable(false));
        if (state && state.error) setPreferredSource('brc');
        else setPreferredSource(Auth.preferredAuthSource());
    }, [state]);

    useEffect(() => {
        FetchAvailableSource();
        (async () => {
            const lookups = await UISettings.lookups();
            const messages = await UISettings.messages();
            setSystemSettings({ ui: { lookups, messages } });
        })();
    }, [FetchAvailableSource, setSystemSettings]);

    return (
        <ThemeProvider theme={Themes.DarkTheme}>
            {/*handles PKCE and auth flow*/}

            <Box height="100%" style={{ background: theme.palette.background.default }} color="text.primary">
                <CSSBaseline />
                <Copyright />
                <Grid container direction="row" justifyContent="center" alignItems="center">
                    <Box textAlign="center" mt={30} mb={5}>
                        {/* <img aria-label="logo" src="/images/logo-new.png" /> */}
                        <img
                            aria-label="logo"
                            src={`/images/logos/Login_CE${BRICATA_VERSION.split('.').length === 3 ? BRICATA_VERSION.split('.').slice(0, 2).join('.') : BRICATA_VERSION}_white.png`}
                        />
                    </Box>
                </Grid>
                <Grid container direction="column" justifyContent="center" alignItems="center">
                    <div>
                        {_get(systemSettings, 'ui.messages.signin', '')}
                    </div>
                </Grid>
                {available ? <Grid container direction="column" justifyContent="center" alignItems="center">
                    {availableSource && !params.error &&
                      <Link
                          style={{ marginLeft: 330 }}
                          component="button"
                          variant="body2"
                          aria-label={`${friendlySrc(availableSource)} Login`}
                          onClick={() => handleUpdatePreferredSource(availableSource)}>
                          {`${friendlySrc(availableSource)} Login`}
                      </Link>
                    }
                    {{
                        saml: <SAMLLogin onSuccess={handleLoginSuccess} onError={handleLoginError}/>,
                        okta: <OktaLogin onSuccess={handleLoginSuccess} onError={handleLoginError}/>,
                    }[preferredSource] || <BRCLogin onSuccess={handleLoginSuccess} onError={handleLoginError}/>}
                    {state && state.error &&
                      <Box style={{ color: theme.palette.error.main }}>
                          {ReactHtmlParser(state.error)}
                      </Box>
                    }
                </Grid>
                    : available === false ? <Grid container direction="column" justifyContent="center" alignItems="center">
                        <Typography>{APP_NAME} is offline</Typography>
                    </Grid>
                        : <Grid container direction="column" justifyContent="center" alignItems="center"></Grid>
                }

            </Box>
        </ThemeProvider>
    );
};

const SecurityWrapper = (props) => {
    const navigate = useNavigate();
    // const { oktaAuth, authState } = useOktaAuth();
    const [authType, setAuthType] = useState('brc');
    const [oktaConf, setOktaConf] = useState({});

    useEffect(() => {
        UISettings.availableAuthSource().then(res => {
            if (res === 'okta') {
                setOktaConf(new OkAuth({
                    issuer: UISettings.settings.auth.okta.issuer,
                    clientId: UISettings.settings.auth.okta.client_id,
                    scopes: ['openid', 'profile', 'email', 'groups'],
                    redirectUri: window.location.origin + '/signin/callback',
                    authParams: {
                        pkce: true,
                        responseMode: 'fragment',
                    },
                }));
                setAuthType(res);
            }
        }, err => {
            console.error(err);
        });
    }, []);

    const oktaAuthHandler = () => {
        navigate('/signin');
    };

    const restoreOriginalUri = (_oktaAuth, originalUri) => {
        navigate(toRelativeUrl(originalUri || '', window.location.origin));
    };

    switch (authType) {
        case 'okta':
            return (
                <Security
                    oktaAuth={oktaConf}
                    onAuthRequired={oktaAuthHandler}
                    restoreOriginalUri={restoreOriginalUri}
                >
                    {props.children}
                </Security>
            );
        default:
            return (
                <React.Fragment>
                    {props.children}
                </React.Fragment>
            );
    }
};

SecurityWrapper.propTypes = {
    children: PropTypes.node.isRequired,
};

export {
    Login as default,
    LoginWrapper,
    SecurityWrapper,
};
